import React, { useEffect, useMemo, useState } from 'react';
import { SlotAvailability } from '../../api/types';
import { useUi } from '../../i18n/ui';

interface Props {
  /** Partenze del tour selezionato (già filtrate: SCHEDULED, status OPEN). */
  slots: SlotAvailability[];
  value?: number; // slotId selezionato
  onChange: (slotId: number) => void;
  months?: number;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const weekdayMon = (d: Date) => (d.getDay() + 6) % 7;

interface Seg {
  slot: SlotAvailability;
  colStart: number;
  colEnd: number;
  isStart: boolean;
  isEnd: boolean;
  lane: number;
}

/**
 * Calendario per scegliere una partenza fissa: ogni partenza è una barra continua
 * cliccabile lunga quanto la partenza. Sostituisce la tendina delle date nel form.
 */
const SlotPickerCalendar: React.FC<Props> = ({ slots, value, onChange, months = 2 }) => {
  const { tr, months: MONTHS, weekdays: WEEKDAYS } = useUi();

  const departures = useMemo(
    () => slots
      .filter((s) => s.startDate)
      .map((s) => ({ slot: s, start: s.startDate, end: s.endDate || s.startDate }))
      .sort((a, b) => a.start.localeCompare(b.start)),
    [slots]
  );

  const today = useMemo(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }, []);
  const firstMonth = useMemo(() => {
    const start = departures.length ? parseISO(departures[0].start) : today;
    const base = start < today ? today : start;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [departures, today]);
  const [cursor, setCursor] = useState(firstMonth);
  useEffect(() => { setCursor(firstMonth); }, [firstMonth]);

  // Salta al mese della partenza selezionata.
  useEffect(() => {
    if (!value) return;
    const d = departures.find((x) => x.slot.id === value);
    if (d) setCursor(new Date(parseISO(d.start).getFullYear(), parseISO(d.start).getMonth(), 1));
  }, [value, departures]);

  const assignLanes = (segs: Seg[]): Seg[] => {
    const laneEnd: number[] = [];
    segs.sort((a, b) => a.colStart - b.colStart || a.slot.startDate.localeCompare(b.slot.startDate));
    segs.forEach((s) => {
      let lane = 0;
      while (lane < laneEnd.length && laneEnd[lane] >= s.colStart) lane++;
      laneEnd[lane] = s.colEnd;
      s.lane = lane;
    });
    return segs;
  };

  const renderWeek = (week: (Date | null)[], key: string) => {
    const dates = week.filter((c): c is Date => !!c);
    if (!dates.length) return null;
    const weekMin = iso(dates[0]);
    const weekMax = iso(dates[dates.length - 1]);

    const segs: Seg[] = [];
    departures.forEach((d) => {
      if (d.start > weekMax || d.end < weekMin) return;
      const segStart = d.start > weekMin ? d.start : weekMin;
      const segEnd = d.end < weekMax ? d.end : weekMax;
      segs.push({
        slot: d.slot,
        colStart: weekdayMon(parseISO(segStart)) + 1,
        colEnd: weekdayMon(parseISO(segEnd)) + 1,
        isStart: d.start >= weekMin,
        isEnd: d.end <= weekMax,
        lane: 0,
      });
    });
    assignLanes(segs);
    const laneCount = segs.reduce((m, s) => Math.max(m, s.lane + 1), 0);

    return (
      <div className="calg__week" key={key}>
        <div className="calg__days">
          {week.map((cell, i) => {
            if (!cell) return <div key={`e${i}`} className="calg__day calg__day--empty" />;
            return (
              <div key={iso(cell)} className="calg__day calg__day--static">
                <span className="cal__num">{cell.getDate()}</span>
              </div>
            );
          })}
        </div>
        {laneCount > 0 && (
          <div className="calg__bars" style={{ gridTemplateRows: `repeat(${laneCount}, 20px)` }}>
            {segs.map((s, i) => {
              const soldOut = s.slot.availableSeats <= 0;
              const selected = s.slot.id === value;
              const cls = [
                'calg__bar', 'calg__bar--pick',
                s.isStart ? 'calg__bar--start' : '',
                s.isEnd ? 'calg__bar--end' : '',
                soldOut ? 'calg__bar--full' : '',
                selected ? 'calg__bar--sel' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  type="button"
                  key={i}
                  className={cls}
                  style={{ gridColumn: `${s.colStart} / ${s.colEnd + 1}`, gridRow: s.lane + 1 }}
                  onClick={() => onChange(s.slot.id)}
                  title={soldOut ? tr('bf.waitlist') : `${s.slot.availableSeats} ${tr('bf.seatsFree')}`}
                >
                  <span className="calg__bar-label">{soldOut ? tr('bf.waitlist') : `${s.slot.availableSeats} ${tr('bf.seatsFree')}`}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMonth = (base: Date) => {
    const year = base.getFullYear();
    const month = base.getMonth();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lead = weekdayMon(first);
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return (
      <div className="cal__month" key={`${year}-${month}`}>
        <div className="cal__month-name">{MONTHS[month]} {year}</div>
        <div className="calg__wd">{WEEKDAYS.map((w) => <div key={w} className="cal__wd">{w}</div>)}</div>
        {weeks.map((w, i) => renderWeek(w, `${year}-${month}-${i}`))}
      </div>
    );
  };

  const monthsToShow = Array.from({ length: months }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth() + i, 1));
  const canGoPrev = cursor > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div className="cal calx">
      <div className="cal__head">
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} disabled={!canGoPrev} aria-label="Mese precedente">‹</button>
        <span className="cal__legend">
          <i className="cal__dot cal__dot--free" /> {tr('cal.legend.free')}
          <i className="cal__dot" style={{ background: 'rgba(244,241,233,.3)', marginLeft: 8 }} /> {tr('cal.pkg.full')}
        </span>
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Mese successivo">›</button>
      </div>
      <div className="cal__months">{monthsToShow.map(renderMonth)}</div>
    </div>
  );
};

export default SlotPickerCalendar;
